import { AccountOption } from '../../utils';
import AccountSelect from '../AccountSelect';
import { Radio, Segment } from 'tea-component';
import { channelColorMap } from '@op/constants';
import { gameInfoType } from '@op/types/common';
import useLanguage from '@op/hooks/useLanguage';
import CardCommon from '@op/components/CardCommon';
import { getXAxisAxisLabel } from '@op/utils/chart';
import EChartCommon from '@op/components/EChartCommon';
import { DBO_EC, DBO_V, DBO_Y } from '@op/utils/format';
import React, { useState, useMemo, useRef } from 'react';
import { useAccountLineData } from './hooks/useAccountLineData';
import { serversSplitText } from '@op/components/FilterCommon/utils/monitorUtil';
import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';

import './index.scss';
import { CHART_METRIC_LIST } from '../../../constants';
import { ClassSpecial } from '@op/constants/common';

export default function ChannelPerformanceTrend({
  filter,
  gameInfo,
  accountFilterOptions,
  defaultSelectAccount,
}: {
  filter: any;
  gameInfo: gameInfoType;
  accountFilterOptions: AccountOption[];
  defaultSelectAccount: string[];
}) {
  const { formatMessage } = useLanguage();
  const eChartsRef = useRef<HTMLDivElement>(null);

  // 渠道、官号Radio
  const [type, setType] = useState('channel');

  // 数据Tab
  const [metric, setMetric] = useState(CHART_METRIC_LIST[0]);

  // 获取图表数据
  const { chartsData, loading, selectAccounts, setSelectedAccounts } =
    useAccountLineData({
      defaultSelectAccount,
      type,
      metric: metric.key,
      filter,
    });
  const options = useMemo(() => {
    if (chartsData?.isEmpty) {
      return {};
    }

    const { series, legend, xAxis, isMin } = chartsData;

    const seriesData = series?.map((item) => {
      const { name, data } = item;

      if (type === 'channel') {
        const color = channelColorMap[name];
        return { name, data, type: 'line', itemStyle: { color } };
      }

      if (type === 'account') {
        return { name, data, type: 'line' };
      }
      return { ...item, type: 'line' };
    });

    const {
      format = (value: any) => DBO_V.reviews(value),
      text: itemName,
      isPercentage = false,
    } = metric;
 
    return {
      series: seriesData,
      legend: {
        data: legend,
        formatter: (params: any) => {
          if (type === 'channel') {
            const { name } = channelInfo(params);
            return `{${dealwithChannelChartKey(params)}|} {name| ${name}}`;
          }

          if (type === 'account') {
            const { name } = channelInfo(params.split(serversSplitText)[1]);
            return `{${dealwithChannelChartKey(
              params.split(serversSplitText)[0],
            )}|} {name| ${name}}`;
          }

          return itemName;
        },
      },
      tooltip: {
        className:
          type === 'account'
            ? `${ClassSpecial.CHART_TOOLTIP_SCROLL} --320`
            : '',
        enterable: type === 'account',
        appendToBody: true,
        formatter: (params: any) => {
          const leftTitle = listTimeTitleFunc(params[0]?.axisValue, 'weekly');
          return formatChartParamsDate({
            params,
            isSort: true,
            listTitleFunc: (item: any) => {
              const isNoOther = item.seriesName !== 'other';
              if (type === 'channel') {
                const { img, name } = channelInfo(item.seriesName);
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${
                  isNoOther ? name : formatMessage({ id: 'Others' })
                }<span>`;
              }

              if (type === 'account') {
                const { img } = channelInfo(
                  item.seriesName.split(serversSplitText)[0],
                );
                return `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${
                  item.seriesName.split(serversSplitText)[1]
                }<span>`;
              }

              return itemName;
            },
            valueFormat: (value) => format(value),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                // if (!item.originValue) return null;
                const share = DBO_V.percentage(
                  Math.abs(item.originValue),
                  total,
                  2,
                );
                return {
                  ...item,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              if (!isPercentage && type !== 'all') {
                newRecords.unshift({
                  text: formatMessage({ id: 'Sum' }),
                  value: DBO_V.reviews(total),
                  share: total === 0 ? '-' : '100%',
                });
              }
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: leftTitle,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage(itemName),
                },
              ];

              if (!isPercentage && type !== 'all') {
                columns.push({
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                });
              }

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },

      xAxis: {
        axisLabel: getXAxisAxisLabel(filter ?? {}),
        data: xAxis || [],
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => {
              return DBO_Y.reviewsPercent(value, isPercentage, isMin);
            },
            color: '#838697',
          },
          nameTextStyle: {
            color: '#444444',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
      ],
      grid: {
        left: 10,
        right: 5,
        top: 20,
      },
    };
  }, [chartsData, formatMessage]);

  return (
    <CardCommon
      title={formatMessage('Official Account Performance Trends by Channel')}
    >
      {/* filter */}
      <div className="dbo-ChannelPerformanceTrend-filter">
        {/* 官号筛选器 */}
        <div className="dbo-ChannelPerformanceTrend-filter-accountSelect">
          <AccountSelect
            options={accountFilterOptions}
            gameInfo={gameInfo}
            defaultValue={selectAccounts}
            onClose={(value) => setSelectedAccounts(value)}
          ></AccountSelect>
        </div>
        {/* 渠道、官号Radio */}
        <div className="dbo-ChannelPerformanceTrend-filter-radio">
          <Radio.Group value={type} onChange={(value) => setType(value)}>
            <Radio name="channel">{formatMessage('By Channel')}</Radio>
            <Radio name="account">{formatMessage('By Official Account')}</Radio>
          </Radio.Group>
        </div>

        {/* 数据Tab */}
        <Segment
          className="tea-segment segment-font12"
          value={metric.key}
          onChange={(value) => {
            const item = CHART_METRIC_LIST.find((it) => it.key === value);
            setMetric(item || CHART_METRIC_LIST[0]);
          }}
          options={CHART_METRIC_LIST.map((item) => {
            return { ...item, text: formatMessage(item.text), value: item.key };
          })}
        />
      </div>

      {/* 图表 */}
      <EChartCommon
        optionsProps={{ type: 'line' }}
        height={460}
        options={options}
        empty={chartsData.isEmpty}
        ref={eChartsRef}
        loading={loading}
      ></EChartCommon>
    </CardCommon>
  );
}
